<template>
  <div class="sec-page">
    <header class="sec-header">
      <button type="button" class="sec-back" @click="goBack">‹ Administration</button>
      <h1 class="sec-title">Security settings</h1>
      <p class="sec-lede">
        How each security alert is raised, who is told when one fires, and the open alerts
        waiting for review. Every change here is audited and raises a configuration-change alert.
      </p>
    </header>

    <div v-if="!canManage && !canReview" class="sec-denied">
      You do not have permission to manage security settings.
    </div>

    <template v-else>
      <p v-if="error" class="sec-error" role="alert">{{ error }}</p>

      <!-- Trello ybyVP1vB: the trigger parameters. An organization may make a
           trigger stricter than the platform row, never quieter (the API
           refuses with policy_below_platform_floor). -->
      <section v-if="canManage" class="sec-card">
        <h2 class="sec-card-title">Alert triggers</h2>
        <p v-if="policiesLoading" class="sec-muted">Loading…</p>
        <table v-else class="sec-table">
          <thead>
            <tr>
              <th scope="col">Trigger</th>
              <th scope="col">Severity</th>
              <th scope="col">Threshold</th>
              <th scope="col">Window</th>
              <th scope="col">On</th>
              <th scope="col">Blocks</th>
              <th scope="col">Set by</th>
              <th scope="col"><span class="sec-sr">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in policies" :key="row.trigger_key">
              <td>
                <div>{{ (row.effective && row.effective.name) || row.trigger_key }}</div>
                <div class="sec-code">{{ row.trigger_key }}</div>
              </td>
              <td>{{ field(row, 'severity') }}</td>
              <td>{{ thresholdOf(row) }}</td>
              <td>{{ windowOf(row) }}</td>
              <td>{{ flag(row, 'is_enabled') }}</td>
              <td>{{ flag(row, 'is_blocking') }}</td>
              <td class="sec-muted">{{ row.organization ? 'This organization' : (row.platform ? 'Platform' : 'Not set') }}</td>
              <td class="sec-row-actions">
                <button type="button" class="sec-btn sec-btn-sm" :disabled="saving" @click="editPolicy(row)">Edit</button>
                <button
                  v-if="row.organization && mayEditOrganization"
                  type="button"
                  class="sec-btn sec-btn-sm"
                  :disabled="saving"
                  @click="removeOverride(row)"
                >
                  Use platform
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <form v-if="policyForm" class="sec-form" @submit.prevent="savePolicy">
          <h3 class="sec-subtitle">{{ policyForm.trigger_key }}</h3>
          <label class="sec-field">
            <span class="sec-label">Change</span>
            <select v-model="policyForm.scope" class="sec-input">
              <option v-if="mayEditOrganization" value="organization">This organization's setting</option>
              <option v-if="mayEditPlatform" value="platform">The platform setting (every organization)</option>
            </select>
          </label>
          <label class="sec-field">
            <span class="sec-label">Severity</span>
            <select v-model="policyForm.severity" class="sec-input">
              <option v-for="s in POLICY_SEVERITIES" :key="s" :value="s">{{ s }}</option>
            </select>
          </label>
          <label class="sec-field">
            <span class="sec-label">Threshold</span>
            <input v-model="policyForm.threshold_value" class="sec-input sec-input-num" type="number" min="0" step="any" />
          </label>
          <label class="sec-field">
            <span class="sec-label">Window (seconds)</span>
            <input v-model="policyForm.window_seconds" class="sec-input sec-input-num" type="number" min="1" />
          </label>
          <label class="sec-check"><input v-model="policyForm.is_enabled" type="checkbox" /> On</label>
          <label class="sec-check"><input v-model="policyForm.is_blocking" type="checkbox" /> Refuse the request</label>
          <div class="sec-actions">
            <button type="submit" class="sec-btn sec-btn-primary" :disabled="saving">{{ saving ? 'Saving…' : 'Save' }}</button>
            <button type="button" class="sec-btn" :disabled="saving" @click="policyForm = null">Cancel</button>
          </div>
        </form>
      </section>

      <!-- Trello wh5sgQgT / NT-X1: the people told when an alert fires. A
           contact is a member of staff who can read the audit log; their own
           verified number and address are used, never one typed here. -->
      <section v-if="canManage" class="sec-card">
        <h2 class="sec-card-title">Security contacts</h2>
        <p class="sec-muted">
          High and critical alerts are texted; everything at or above a contact's minimum is e-mailed.
          Tier 2 contacts are told when a high or critical alert is still open after the escalation window.
        </p>
        <p v-if="!contacts.length" class="sec-empty">
          Nobody in this organization is a security contact, so alerts go to the platform's contacts.
        </p>
        <table v-else class="sec-table">
          <thead>
            <tr>
              <th scope="col">Person</th>
              <th scope="col">Told of</th>
              <th scope="col">Channels</th>
              <th scope="col">Reachable</th>
              <th scope="col">Tier</th>
              <th scope="col">Active</th>
              <th scope="col"><span class="sec-sr">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="contact in contacts" :key="contact.id">
              <td>{{ contact.user ? contact.user.name : '—' }} <span v-if="contact.scope === 'platform'" class="sec-code">platform</span></td>
              <td>{{ contact.min_severity }} and worse</td>
              <td>{{ contact.channels.join(', ') }}</td>
              <td :class="{ 'sec-warn': contact.reachable.length < contact.channels.length }">
                {{ contact.reachable.length ? contact.reachable.join(', ') : 'not reachable' }}
              </td>
              <td>{{ contact.tier }}</td>
              <td>
                <input type="checkbox" :checked="contact.is_active" :disabled="saving" :aria-label="'Active'" @change="toggleContact(contact)" />
              </td>
              <td class="sec-row-actions">
                <button type="button" class="sec-btn sec-btn-sm sec-btn-danger" :disabled="saving" @click="removeContact(contact)">Remove</button>
              </td>
            </tr>
          </tbody>
        </table>

        <form class="sec-form" @submit.prevent="addContact">
          <label class="sec-field">
            <span class="sec-label">Person</span>
            <select v-model="contactForm.userId" class="sec-input">
              <option value="">Select…</option>
              <option v-for="u in staff" :key="u.public_id || u.id" :value="u.public_id || u.id">{{ personName(u) }}</option>
            </select>
          </label>
          <label class="sec-field">
            <span class="sec-label">Tell them of</span>
            <select v-model="contactForm.minSeverity" class="sec-input">
              <option v-for="s in ALERT_SEVERITIES" :key="s" :value="s">{{ s }} and worse</option>
            </select>
          </label>
          <label v-for="c in CONTACT_CHANNELS" :key="c" class="sec-check">
            <input v-model="contactForm.channels" type="checkbox" :value="c" /> {{ c === 'sms' ? 'Text' : 'E-mail' }}
          </label>
          <label class="sec-field">
            <span class="sec-label">Tier</span>
            <select v-model.number="contactForm.tier" class="sec-input sec-input-num">
              <option :value="1">1 — at once</option>
              <option :value="2">2 — on escalation</option>
            </select>
          </label>
          <div class="sec-actions">
            <button type="submit" class="sec-btn sec-btn-primary" :disabled="saving || !contactForm.userId || !contactForm.channels.length">
              Add contact
            </button>
          </div>
        </form>
      </section>

      <!-- Trello bMZnxWqc / 3WsAGws1: the routine review. Acknowledging stops
           the escalation; the person an alert is about cannot review it. -->
      <section v-if="canReview" class="sec-card">
        <h2 class="sec-card-title">Open alerts</h2>
        <p v-if="alertsLoading" class="sec-muted">Loading…</p>
        <p v-else-if="!openAlerts.length" class="sec-empty">No open alerts.</p>
        <table v-else class="sec-table">
          <thead>
            <tr>
              <th scope="col">When</th>
              <th scope="col">Alert</th>
              <th scope="col">Severity</th>
              <th scope="col">About</th>
              <th scope="col"><span class="sec-sr">Review</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="alert in openAlerts" :key="alert.id">
              <td>{{ formatDate(alert.occurred_at) }}</td>
              <td>
                <div>{{ alert.type }}</div>
                <div class="sec-muted">{{ alert.reason }}</div>
              </td>
              <td>{{ alert.severity }}</td>
              <td>{{ alert.subject_username || '—' }}</td>
              <td class="sec-row-actions">
                <button type="button" class="sec-btn sec-btn-sm" :disabled="saving" @click="review(alert, 'acknowledged')">Acknowledge</button>
                <button type="button" class="sec-btn sec-btn-sm" :disabled="saving" @click="review(alert, 'resolved')">Resolve</button>
                <button type="button" class="sec-btn sec-btn-sm" :disabled="saving" @click="review(alert, 'suppressed')">False positive</button>
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    </template>
  </div>
</template>

<script>
import { useSessionStore } from "@/stores/session";
import {
  listAlertTriggerPolicies,
  updateAlertTriggerPolicy,
  removeAlertTriggerPolicyOverride,
  listAlertSubscriptions,
  createAlertSubscription,
  updateAlertSubscription,
  deleteAlertSubscription,
  POLICY_SEVERITIES,
  ALERT_SEVERITIES,
  CONTACT_CHANNELS,
} from "@/api2/securitySettings";
import { listSystemAlerts, reviewSystemAlert } from "@/api2/auditLogs";
import { listUsers } from "@/api2/users";

/** The API's refusals, in words an administrator can act on. */
const MESSAGES = {
  policy_below_platform_floor: "An organization may make a trigger stricter than the platform setting, not quieter.",
  policy_always_on: "The audit-log integrity alarm cannot be switched off.",
  subscriber_not_eligible: "A security contact must be an active member of staff who can read the audit log.",
  subscriber_unreachable: "That person has no verified number or e-mail address for the channels chosen.",
  already_subscribed: "That person is already a security contact.",
  alert_review_by_party: "An alert cannot be reviewed by the person it is about or who caused it.",
  platform_policy_forbidden: "Only a platform operator may change the platform setting.",
};

export function securityErrorMessage(error, fallback) {
  const code = error && (error.code || (error.payload && error.payload.code));
  return MESSAGES[code] || (error && error.message) || fallback;
}

export default {
  name: "SecuritySettingsPage",
  data() {
    return {
      policies: [],
      mayEditOrganization: false,
      mayEditPlatform: false,
      policiesLoading: false,
      policyForm: null,
      contacts: [],
      staff: [],
      contactForm: { userId: "", channels: ["email"], minSeverity: "high", tier: 1 },
      openAlerts: [],
      alertsLoading: false,
      saving: false,
      error: "",
      POLICY_SEVERITIES,
      ALERT_SEVERITIES,
      CONTACT_CHANNELS,
    };
  },
  computed: {
    session() {
      return useSessionStore();
    },
    /** org:admin, or a platform operator with platform:configure; the API decides each call. */
    canManage() {
      return this.session.can("org:admin")
        || (Boolean(this.session.me && this.session.me.is_platform_operator) && this.session.can("platform:configure"));
    },
    canReview() {
      return this.session.can("audit:read");
    },
  },
  async mounted() {
    const work = [];
    if (this.canManage) work.push(this.loadPolicies(), this.loadContacts(), this.loadStaff());
    if (this.canReview) work.push(this.loadAlerts());
    await Promise.all(work);
  },
  methods: {
    goBack() {
      this.$router.push("/administration");
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString() : "";
    },
    personName(user) {
      return [user.first_name, user.last_name].filter(Boolean).join(" ") || user.username || user.email || "";
    },
    /** What is in force for a trigger: the override if any, else the platform row. */
    inForce(row) {
      return row.effective || row.organization || row.platform || {};
    },
    field(row, key) {
      const value = this.inForce(row)[key];
      return value === null || value === undefined ? "—" : value;
    },
    flag(row, key) {
      return this.inForce(row)[key] ? "Yes" : "No";
    },
    thresholdOf(row) {
      const policy = this.inForce(row);
      if (policy.threshold_value === null || policy.threshold_value === undefined) return "—";
      return `${policy.threshold_value} ${policy.threshold_unit || ""}`.trim();
    },
    windowOf(row) {
      const seconds = this.inForce(row).window_seconds;
      return seconds ? `${seconds} s` : "—";
    },
    async loadPolicies() {
      this.policiesLoading = true;
      try {
        const result = await listAlertTriggerPolicies();
        this.policies = result.rows;
        this.mayEditOrganization = result.mayEditOrganization;
        this.mayEditPlatform = result.mayEditPlatform;
      } catch (e) {
        this.error = securityErrorMessage(e, "Could not load the alert triggers.");
      } finally {
        this.policiesLoading = false;
      }
    },
    async loadContacts() {
      try {
        this.contacts = await listAlertSubscriptions();
      } catch (e) {
        this.error = securityErrorMessage(e, "Could not load the security contacts.");
      }
    },
    async loadStaff() {
      try {
        const page = await listUsers({ per_page: 100 });
        this.staff = ((page && page.items) || []).filter((u) => !["patient", "contact"].includes(u.account_type));
      } catch (e) {
        this.staff = [];
      }
    },
    async loadAlerts() {
      this.alertsLoading = true;
      try {
        const page = await listSystemAlerts({ "filter[status]": "open", per_page: 25, sort: "severity" });
        this.openAlerts = (page && page.items) || [];
      } catch (e) {
        this.error = securityErrorMessage(e, "Could not load the open alerts.");
      } finally {
        this.alertsLoading = false;
      }
    },
    editPolicy(row) {
      const policy = this.inForce(row);
      this.policyForm = {
        trigger_key: row.trigger_key,
        scope: this.mayEditOrganization ? "organization" : "platform",
        severity: policy.severity || "medium",
        threshold_value: policy.threshold_value === null || policy.threshold_value === undefined ? "" : policy.threshold_value,
        window_seconds: policy.window_seconds || "",
        is_enabled: policy.is_enabled !== false,
        is_blocking: Boolean(policy.is_blocking),
      };
    },
    async savePolicy() {
      const form = this.policyForm;
      this.saving = true;
      this.error = "";
      try {
        await updateAlertTriggerPolicy(form.trigger_key, form.scope, {
          severity: form.severity,
          threshold_value: form.threshold_value === "" ? null : Number(form.threshold_value),
          window_seconds: form.window_seconds === "" ? null : Number(form.window_seconds),
          is_enabled: form.is_enabled,
          is_blocking: form.is_blocking,
        });
        this.policyForm = null;
        await this.loadPolicies();
      } catch (e) {
        this.error = securityErrorMessage(e, "The trigger could not be saved.");
      } finally {
        this.saving = false;
      }
    },
    async removeOverride(row) {
      this.saving = true;
      this.error = "";
      try {
        await removeAlertTriggerPolicyOverride(row.trigger_key);
        await this.loadPolicies();
      } catch (e) {
        this.error = securityErrorMessage(e, "The override could not be removed.");
      } finally {
        this.saving = false;
      }
    },
    async addContact() {
      this.saving = true;
      this.error = "";
      try {
        await createAlertSubscription({ ...this.contactForm });
        this.contactForm = { userId: "", channels: ["email"], minSeverity: "high", tier: 1 };
        await this.loadContacts();
      } catch (e) {
        this.error = securityErrorMessage(e, "The contact could not be added.");
      } finally {
        this.saving = false;
      }
    },
    async toggleContact(contact) {
      this.saving = true;
      try {
        await updateAlertSubscription(contact.id, { isActive: !contact.is_active });
        await this.loadContacts();
      } catch (e) {
        this.error = securityErrorMessage(e, "The contact could not be changed.");
      } finally {
        this.saving = false;
      }
    },
    async removeContact(contact) {
      this.saving = true;
      try {
        await deleteAlertSubscription(contact.id);
        await this.loadContacts();
      } catch (e) {
        this.error = securityErrorMessage(e, "The contact could not be removed.");
      } finally {
        this.saving = false;
      }
    },
    async review(alert, status) {
      let resolutionNotes = "";
      if (status !== "acknowledged") {
        resolutionNotes = window.prompt(status === "suppressed" ? "Why is this a false positive?" : "How was this resolved?") || "";
        if (!resolutionNotes.trim()) return;
      }

      this.saving = true;
      this.error = "";
      try {
        await reviewSystemAlert(alert.id, { status, resolutionNotes });
        await this.loadAlerts();
      } catch (e) {
        this.error = securityErrorMessage(e, "The alert could not be reviewed.");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.sec-page {
  padding: 24px;
  max-width: 1100px;
  margin: 0 auto;
}
.sec-back {
  background: none;
  border: 0;
  color: #2a6fb5;
  cursor: pointer;
  padding: 0 0 8px;
  font-size: 0.95rem;
}
.sec-title {
  font-size: 1.6rem;
  margin: 0 0 6px;
}
.sec-lede,
.sec-muted {
  color: #5a6472;
}
.sec-lede {
  margin: 0 0 20px;
  max-width: 70ch;
}
.sec-denied,
.sec-error {
  background: #fdecea;
  border: 1px solid #f5c2bd;
  color: #8c231a;
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 16px;
}
.sec-card {
  background: #fff;
  border: 1px solid #e3e7ed;
  border-radius: 8px;
  padding: 18px;
  margin-bottom: 18px;
}
.sec-card-title {
  font-size: 1.05rem;
  margin: 0 0 14px;
}
.sec-subtitle {
  width: 100%;
  font-size: 0.95rem;
  margin: 0;
}
.sec-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}
.sec-table th,
.sec-table td {
  border-bottom: 1px solid #eef1f5;
  padding: 8px 6px;
  text-align: left;
  vertical-align: top;
}
.sec-code {
  font-family: monospace;
  font-size: 0.8rem;
  color: #5a6472;
}
.sec-warn {
  color: #8c5a00;
}
.sec-empty {
  color: #5a6472;
  font-style: italic;
}
.sec-form {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: flex-end;
  margin-top: 16px;
}
.sec-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.sec-label {
  font-size: 0.85rem;
  color: #5a6472;
}
.sec-input {
  border: 1px solid #ccd3dc;
  border-radius: 5px;
  padding: 7px 9px;
  min-width: 180px;
}
.sec-input-num {
  min-width: 90px;
  width: 110px;
}
.sec-check {
  display: flex;
  align-items: center;
  gap: 6px;
}
.sec-actions,
.sec-row-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.sec-btn {
  border: 1px solid #ccd3dc;
  background: #fff;
  border-radius: 5px;
  padding: 7px 12px;
  cursor: pointer;
}
.sec-btn-sm {
  padding: 4px 8px;
  font-size: 0.85rem;
}
.sec-btn-primary {
  background: #2a6fb5;
  border-color: #2a6fb5;
  color: #fff;
}
.sec-btn-danger {
  color: #8c231a;
}
.sec-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
}
</style>
